package styles

import org.jetbrains.compose.web.css.*

object AppStyleSheet : StyleSheet() {
    // Design tokens for consistency
    private fun varColor(name: String) = Color("var(--$name)")
    private val colorPrimary = varColor("primary-color")
    private val colorDanger = varColor("danger-color")
    private val colorSuccess = Color("#28a745")
    private val colorWarning = Color("#ffc107")
    private val colorText = varColor("text-color")
    private val colorTextSecondary = Color("#666")
    private val colorTextMuted = Color("#999")
    private val colorBorder = Color("#ddd")
    private val colorBackground = Color("#f8f9fa")
    private val colorWhite = Color.white
    private val shadowLight = "0 1px 3px rgba(0,0,0,0.1)"
    private val shadowMedium = "0 2px 8px rgba(0,0,0,0.12)"
    private val borderRadiusSmall = 4.px
    private val borderRadiusMedium = 8.px

    // Container
    val container by style {
        maxWidth(800.px)
        margin(0.px, "auto".unsafeCast<CSSNumeric>())
        padding(16.px)
        fontFamily("system-ui", "-apple-system", "BlinkMacSystemFont", "Segoe UI", "sans-serif")
        backgroundColor(varColor("bg-color"))
        minHeight(100.vh)

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                padding(12.px)
            }
        }
    }
    val header by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
        marginBottom(24.px)
        paddingBottom(16.px)
        property("border-bottom", "1px solid ${varColor("border-color")}")

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                flexWrap(FlexWrap.Wrap)
                gap(12.px)
                paddingBottom(12.px)
            }
        }
    }
    val headerButtons by style {
        display(DisplayStyle.Flex)
        gap(8.px)
        alignItems(AlignItems.Center)

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                gap(6.px)
            }
        }
    }
    val primaryButton by style {
        padding(8.px, 16.px)
        backgroundColor(colorPrimary)
        color(colorWhite)
        border(0.px)
        borderRadius(borderRadiusSmall)
        cursor("pointer")
        property("box-shadow", shadowMedium)
        property("font-weight", "500")
        property("transition", "all 0.2s ease")
        self + hover style {
            backgroundColor(Color("#0056b3"))
            property("box-shadow", "0 4px 12px rgba(0,0,0,0.2)")
            property("transform", "translateY(-1px)")
        }
    }
    val secondaryButton by style {
        padding(10.px, 24.px)
        backgroundColor(colorWhite)
        color(colorPrimary)
        border(1.px, LineStyle.Solid, colorPrimary)
        borderRadius(borderRadiusSmall)
        cursor("pointer")
        property("transition", "all 0.2s ease")
        self + hover style {
            backgroundColor(colorPrimary)
            color(colorWhite)
        }
    }
    val logoutButton by style {
        padding(8.px, 16.px)
        backgroundColor(colorDanger)
        color(colorWhite)
        border(0.px)
        borderRadius(borderRadiusSmall)
        cursor("pointer")
        property("transition", "background-color 0.2s ease")
        self + hover style {
            backgroundColor(Color("#c82333"))
        }
    }
    val iconButton by style {
        padding(8.px)
        backgroundColor(Color("transparent"))
        color(colorText)
        border(1.px, LineStyle.Solid, colorBorder)
        borderRadius(borderRadiusSmall)
        cursor("pointer")
        fontSize(20.px)
        property("transition", "all 0.2s ease")
        self + hover style {
            backgroundColor(colorBackground)
        }
    }

    // Responsive design - Mobile support
    val mobileContainer by style {
        padding(12.px)
    }

    // Loading states
    val loading by style {
        textAlign("center")
        padding(48.px)
        color(colorTextSecondary)
    }
    val loadingSpinner by style {
        display(DisplayStyle.InlineBlock)
        width(40.px)
        height(40.px)
        border(3.px, LineStyle.Solid, colorBorder)
        property("border-top", "3px solid ${colorPrimary.toString()}")
        borderRadius(50.percent)
        property("animation", "spin 1s linear infinite")
    }
    val skeleton by style {
        backgroundColor(colorBackground)
        borderRadius(borderRadiusSmall)
        property("animation", "pulse 1.5s ease-in-out infinite")
    }
    val skeletonText by style {
        backgroundColor(colorBackground)
        borderRadius(borderRadiusSmall)
        property("animation", "pulse 1.5s ease-in-out infinite")
        height(16.px)
        marginBottom(8.px)
    }
    val skeletonButton by style {
        backgroundColor(colorBackground)
        borderRadius(borderRadiusSmall)
        property("animation", "pulse 1.5s ease-in-out infinite")
        height(40.px)
        width(100.percent)
    }

    // Form validation states
    val formGroup by style {
        marginBottom(16.px)
    }
    val inputValid by style {
        property("border-color", colorSuccess.toString())
    }
    val inputInvalid by style {
        property("border-color", colorDanger.toString())
    }
    val validationMessage by style {
        fontSize(12.px)
        marginTop(4.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(4.px)
    }
    val validationSuccess by style {
        color(colorSuccess)
    }
    val validationError by style {
        color(colorDanger)
    }
    val inputHint by style {
        fontSize(12.px)
        color(colorTextMuted)
        marginTop(4.px)
    }

    // Search and filter styles
    val searchSection by style {
        backgroundColor(Color("#f8f9fa"))
        padding(16.px, 20.px)
        style {
            property("border-bottom", "1px solid #dee2e6")
        }

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                padding(12.px)
            }
        }
    }
    val searchBar by style {
        display(DisplayStyle.Flex)
        gap(8.px)
        style {
            property("margin-bottom", "12px")
        }

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                flexDirection(FlexDirection.Column)
            }
        }
    }
    val searchInput by style {
        style {
            property("flex", "1")
        }
        padding(10.px, 16.px)
        border(1.px, LineStyle.Solid, Color("#ced4da"))
        borderRadius(borderRadiusSmall)
        fontSize(16.px)
        property("transition", "border-color 0.2s ease")
        self + focus style {
            outline("none")
            border(2.px, LineStyle.Solid, colorPrimary)
        }
    }
    val clearSearchButton by style {
        padding(10.px, 16.px)
        backgroundColor(colorWhite)
        color(Color("#6c757d"))
        border(1.px, LineStyle.Solid, Color("#ced4da"))
        borderRadius(borderRadiusSmall)
        cursor("pointer")
        property("transition", "all 0.2s ease")
        self + hover style {
            backgroundColor(Color("#f8f9fa"))
            color(Color("#495057"))
        }
    }
    val controlsBar by style {
        display(DisplayStyle.Flex)
        gap(16.px)
        alignItems(AlignItems.Center)
        flexWrap(FlexWrap.Wrap)

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Stretch)
                gap(8.px)
            }
        }
    }
    val controlGroup by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(8.px)
    }
    val selectInput by style {
        padding(8.px, 12.px)
        border(1.px, LineStyle.Solid, Color("#ced4da"))
        borderRadius(borderRadiusSmall)
        backgroundColor(colorWhite)
        fontSize(14.px)
        cursor("pointer")
        property("transition", "border-color 0.2s ease")
        self + focus style {
            outline("none")
            border(2.px, LineStyle.Solid, colorPrimary)
        }
    }
    val postList by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(16.px)
    }
    val postCard by style {
        padding(20.px)
        backgroundColor(colorWhite)
        borderRadius(borderRadiusMedium)
        property("box-shadow", shadowLight)
        cursor("pointer")
        property("transition", "box-shadow 0.2s ease, transform 0.2s ease")

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                padding(12.px)
            }
        }

        self + hover style {
            property("box-shadow", shadowMedium)
            property("transform", "translateY(-2px)")
        }
    }
    val postContent by style {
        color(colorTextSecondary)
        lineHeight("1.5")
        marginTop(8.px)
    }
    val error by style {
        color(colorDanger)
        textAlign("center")
        padding(32.px)
        backgroundColor(Color("#fff5f5"))
        borderRadius(borderRadiusMedium)
        border(1.px, LineStyle.Solid, Color("#ffcccc"))
    }

    // Authentication screen styles
    val loginContainer by style {
        padding(16.px)
        minHeight(80.vh)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.Center)

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                padding(12.px)
            }
        }
    }
    val loginForm by style {
        maxWidth(400.px)
        margin(0.px, "auto".unsafeCast<CSSNumeric>())
        padding(32.px)
        backgroundColor(colorWhite)
        borderRadius(borderRadiusMedium)
        property("box-shadow", shadowMedium)

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                maxWidth(100.percent)
                padding(24.px)
                borderRadius(borderRadiusSmall)
            }
        }
    }
    val loginTitle by style {
        marginTop(0.px)
        marginBottom(24.px)
        textAlign("center")
        color(colorText)

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                fontSize(20.px)
                marginBottom(16.px)
            }
        }
    }
    val loginButtonGroup by style {
        marginTop(24.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(8.px)
    }

    // PostDetailScreen styles
    val detailContainer by style {
        padding(16.px)

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                padding(12.px)
            }
        }
    }
    val backButton by style {
        marginBottom(16.px)
        padding(8.px, 16.px)
        backgroundColor(colorWhite)
        border(1.px, LineStyle.Solid, colorBorder)
        borderRadius(borderRadiusSmall)
        cursor("pointer")
        color(colorText)
        property("transition", "background-color 0.2s ease")
        self + hover style {
            backgroundColor(Color("#f0f0f0"))
        }
    }
    val postDetail by style {
        marginBottom(32.px)
        padding(24.px)
        backgroundColor(colorWhite)
        borderRadius(borderRadiusMedium)
        property("box-shadow", shadowLight)

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                padding(16.px)
                marginBottom(20.px)
            }
        }
    }
    val postTitle by style {
        marginTop(0.px)
        marginBottom(8.px)
        fontSize(24.px)
        color(colorText)

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                fontSize(20.px)
            }
        }
    }
    val postMeta by style {
        color(colorTextSecondary)
        fontSize(14.px)
        marginBottom(16.px)
    }
    val postBody by style {
        lineHeight("1.8")
        whiteSpace("pre-wrap")
        color(colorText)
    }
    val commentsSection by style {
        marginTop(24.px)
    }
    val commentList by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        marginBottom(24.px)
    }
    val commentCard by style {
        padding(16.px)
        backgroundColor(colorWhite)
        borderRadius(borderRadiusMedium)
        property("box-shadow", "0 1px 2px rgba(0,0,0,0.08)")
        property("transition", "box-shadow 0.2s ease")
        self + hover style {
            property("box-shadow", shadowLight)
        }
    }
    val commentHeader by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
        marginBottom(8.px)

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Stretch)
                gap(4.px)
            }
        }
    }
    val commentAuthor by style {
        fontWeight("bold")
        color(colorText)
    }
    val commentDate by style {
        fontSize(12.px)
        color(colorTextMuted)
    }
    val commentContent by style {
        margin(0.px)
        color(Color("#444"))
        lineHeight("1.5")
    }
    val noComments by style {
        color(colorTextMuted)
        textAlign("center")
        padding(24.px)
        backgroundColor(colorWhite)
        borderRadius(borderRadiusMedium)
    }
    val commentForm by style {
        padding(24.px)
        backgroundColor(colorWhite)
        borderRadius(borderRadiusMedium)
        property("box-shadow", shadowLight)

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                padding(16.px)
            }
        }
    }
    val textInput by style {
        width(100.percent)
        padding(10.px, 12.px)
        fontSize(14.px)
        border(1.px, LineStyle.Solid, colorBorder)
        borderRadius(borderRadiusSmall)
        boxSizing("border-box")
        property("transition", "border-color 0.2s ease, box-shadow 0.2s ease")
        self + focus style {
            property("outline", "none")
            property("border-color", colorPrimary.toString())
            property("box-shadow", "0 0 0 3px rgba(0,123,255,0.1)")
        }
    }
    val userValid by style {
        property("border-color", colorSuccess.toString())
    }
    val userInvalidStyle by style {
        property("border-color", colorDanger.toString())
    }
    val textArea by style {
        width(100.percent)
        minHeight(100.px)
        padding(10.px, 12.px)
        fontSize(14.px)
        border(1.px, LineStyle.Solid, colorBorder)
        borderRadius(borderRadiusSmall)
        boxSizing("border-box")
        property("resize", "vertical")
        property("transition", "border-color 0.2s ease, box-shadow 0.2s ease")
        self + focus style {
            property("outline", "none")
            property("border-color", colorPrimary.toString())
            property("box-shadow", "0 0 0 3px rgba(0,123,255,0.1)")
        }
    }
    val formError by style {
        // Use darker red (#b91c1c) for WCAG AA compliance (7.2:1 contrast with #fff5f5)
        color(Color("#b91c1c"))
        fontSize(14.px)
        marginBottom(12.px)
        padding(8.px, 12.px)
        backgroundColor(Color("#fff5f5"))
        borderRadius(borderRadiusSmall)
        border(1.px, LineStyle.Solid, Color("#ffcccc"))
    }
    val submitButton by style {
        width(100.percent)
        padding(12.px, 24.px)
        backgroundColor(colorPrimary)
        color(colorWhite)
        border(0.px)
        borderRadius(borderRadiusSmall)
        fontSize(14.px)
        fontWeight("500")
        cursor("pointer")
        property("transition", "background-color 0.2s ease, transform 0.1s ease")
        self + hover style {
            backgroundColor(Color("#0056b3"))
        }
        self + active style {
            property("transform", "scale(0.98)")
        }
        self + disabled style {
            backgroundColor(Color("#6c757d"))
            cursor("not-allowed")
            property("transform", "none")
        }
    }

    // AdminPostEditorScreen styles
    val editorContainer by style {
        padding(16.px)
        minHeight(80.vh)

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                padding(12.px)
            }
        }
    }

    // Image upload button style (green)
    val imageUploadButton by style {
        marginTop(8.px)
        padding(8.px, 16.px)
        backgroundColor(Color("#16a34a"))  // green-600
        color(Color("#ffffff"))
        border(0.px)
        borderRadius(borderRadiusSmall)
        cursor("pointer")
        property("transition", "background-color 0.2s ease")

        self + hover style {
            backgroundColor(Color("#15803d"))  // green-700
        }

        self + disabled style {
            backgroundColor(Color("#6c757d"))
            cursor("not-allowed")
        }
    }

    // Upload status message (blue text)
    val uploadStatus by style {
        marginTop(8.px)
        fontSize(14.px)
        color(Color("#2563eb"))  // blue-600
    }

    // Upload error message (red text)
    val uploadError by style {
        marginTop(8.px)
        fontSize(14.px)
        color(Color("#dc3545"))  // red-600
    }

    // Image preview container
    val imagePreview by style {
        marginTop(16.px)
    }

    // Image preview image
    val imagePreviewImg by style {
        marginTop(8.px)
        maxWidth(100.percent)
        height("auto".unsafeCast<CSSSizeValue<CSSUnit>>())
        maxHeight(256.px)
        borderRadius(borderRadiusSmall)
        border(1.px, LineStyle.Solid, Color("#dee2e6"))
    }

    // Uploaded image item container
    val uploadedImageItem by style {
        marginTop(8.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        padding(8.px)
        backgroundColor(Color("#f8f9fa"))  // gray-50
        borderRadius(borderRadiusSmall)
    }

    // Uploaded image name
    val uploadedImageName by style {
        fontSize(14.px)
        flex(1, 1, 1.unsafeCast<CSSNumeric>())
    }

    // Button container for image actions
    val imageActionButtons by style {
        display(DisplayStyle.Flex)
        gap(8.px)
    }

    // Small blue button for image actions
    val imageActionButton by style {
        padding(4.px, 12.px)
        fontSize(12.px)
        backgroundColor(Color("#2563eb"))  // blue-600
        color(Color("#ffffff"))
        border(0.px)
        borderRadius(borderRadiusSmall)
        cursor("pointer")
        property("transition", "background-color 0.2s ease")

        self + hover style {
            backgroundColor(Color("#1d4ed8"))  // blue-700
        }
    }

    // Small red button for delete
    val imageDeleteButton by style {
        padding(4.px, 12.px)
        fontSize(12.px)
        backgroundColor(Color("#dc3545"))  // red-600
        color(Color("#ffffff"))
        border(0.px)
        borderRadius(borderRadiusSmall)
        cursor("pointer")
        property("transition", "background-color 0.2s ease")

        self + hover style {
            backgroundColor(Color("#b91c1c"))  // red-700
        }
    }

    val editorForm by style {
        maxWidth(600.px)
        margin(0.px, "auto".unsafeCast<CSSNumeric>())
        padding(32.px)
        backgroundColor(colorWhite)
        borderRadius(borderRadiusMedium)
        property("box-shadow", shadowMedium)

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                maxWidth(100.percent)
                padding(16.px)
            }
        }
    }
    val editorTitle by style {
        marginTop(0.px)
        marginBottom(24.px)
        textAlign("center")
        color(colorText)
    }
    val editorTextArea by style {
        width(100.percent)
        minHeight(200.px)
        padding(10.px, 12.px)
        fontSize(14.px)
        border(1.px, LineStyle.Solid, colorBorder)
        borderRadius(borderRadiusSmall)
        boxSizing("border-box")
        property("resize", "vertical")
        property("transition", "border-color 0.2s ease, box-shadow 0.2s ease")
        self + focus style {
            property("outline", "none")
            property("border-color", colorPrimary.toString())
            property("box-shadow", "0 0 0 3px rgba(0,123,255,0.1)")
        }
    }
    val editorButtonGroup by style {
        marginTop(24.px)
        textAlign("center")
    }

    // Login mode toggle styles
    val loginModeToggle by style {
        display(DisplayStyle.Flex)
        marginBottom(20.px)
        borderRadius(borderRadiusSmall)
        overflow("hidden")
        border(1.px, LineStyle.Solid, colorBorder)
    }
    val toggleButton by style {
        flex(1)
        padding(10.px, 16.px)
        backgroundColor(colorWhite)
        border(0.px)
        cursor("pointer")
        fontSize(14.px)
        color(colorTextSecondary)
        property("transition", "background-color 0.2s ease")
        self + hover style {
            backgroundColor(Color("#f5f5f5"))
        }
    }
    val toggleButtonActive by style {
        flex(1)
        padding(10.px, 16.px)
        backgroundColor(colorPrimary)
        color(colorWhite)
        border(0.px)
        cursor("pointer")
        fontSize(14.px)
    }
    val registerLink by style {
        marginTop(20.px)
        textAlign("center")
        fontSize(14.px)
        color(colorTextSecondary)
    }
    val linkStyle by style {
        color(colorPrimary)
        cursor("pointer")
        property("text-decoration", "underline")
        self + hover style {
            color(Color("#0056b3"))
        }
    }

    // TOTP styles
    val totpDescription by style {
        color(colorTextSecondary)
        fontSize(14.px)
        lineHeight("1.6")
        marginBottom(20.px)
        textAlign("center")
    }
    val totpInput by style {
        width(100.percent)
        padding(16.px)
        fontSize(24.px)
        textAlign("center")
        letterSpacing(8.px)
        border(1.px, LineStyle.Solid, colorBorder)
        borderRadius(borderRadiusSmall)
        boxSizing("border-box")
        property("font-family", "monospace")
        self + focus style {
            property("outline", "none")
            property("border-color", colorPrimary.toString())
            property("box-shadow", "0 0 0 3px rgba(0,123,255,0.1)")
        }
    }
    val totpQrSection by style {
        textAlign("center")
        marginBottom(24.px)
        padding(20.px)
        backgroundColor(colorBackground)
        borderRadius(borderRadiusMedium)

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                padding(12.px)
            }
        }
    }
    val secretKey by style {
        fontFamily("monospace")
        fontSize(14.px)
        padding(12.px)
        backgroundColor(colorWhite)
        borderRadius(borderRadiusSmall)
        border(1.px, LineStyle.Solid, colorBorder)
        property("word-break", "break-all")
        marginTop(8.px)
    }
    val skipLink by style {
        marginTop(16.px)
        textAlign("center")
        fontSize(14.px)
    }
    val totpSetupPrompt by style {
        padding(12.px, 16.px)
        backgroundColor(Color("#fff3cd"))
        borderRadius(borderRadiusSmall)
        marginBottom(20.px)
        fontSize(14.px)
        color(Color("#856404"))
        border(1.px, LineStyle.Solid, Color("#ffc107"))
    }

    // Detail header with edit button
    val detailHeader by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
        marginBottom(16.px)

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Stretch)
                gap(8.px)
            }
        }
    }
    val editButton by style {
        padding(8.px, 16.px)
        backgroundColor(colorPrimary)
        color(colorWhite)
        border(0.px)
        borderRadius(borderRadiusSmall)
        cursor("pointer")
        property("transition", "background-color 0.2s ease")
        self + hover style {
            backgroundColor(Color("#0056b3"))
        }
    }
    val deleteButton by style {
        padding(12.px, 24.px)
        backgroundColor(colorDanger)
        color(colorWhite)
        border(0.px)
        borderRadius(borderRadiusSmall)
        fontSize(14.px)
        fontWeight("500")
        cursor("pointer")
        marginRight(12.px)
        property("transition", "background-color 0.2s ease")
        self + hover style {
            backgroundColor(Color("#c82333"))
        }
    }
    val cancelButton by style {
        padding(12.px, 24.px)
        backgroundColor(colorWhite)
        color(colorText)
        border(1.px, LineStyle.Solid, colorBorder)
        borderRadius(borderRadiusSmall)
        fontSize(14.px)
        cursor("pointer")
        marginRight(12.px)
        property("transition", "background-color 0.2s ease")
        self + hover style {
            backgroundColor(Color("#f5f5f5"))
        }
    }

    // Status selector styles
    val statusSelector by style {
        display(DisplayStyle.Flex)
        gap(8.px)
    }
    val statusButton by style {
        padding(8.px, 16.px)
        backgroundColor(colorWhite)
        color(colorTextSecondary)
        border(1.px, LineStyle.Solid, colorBorder)
        borderRadius(borderRadiusSmall)
        cursor("pointer")
        fontSize(14.px)
        property("transition", "all 0.2s ease")
        self + hover style {
            backgroundColor(Color("#f5f5f5"))
        }
    }
    val statusButtonActive by style {
        padding(8.px, 16.px)
        backgroundColor(colorPrimary)
        color(colorWhite)
        border(1.px, LineStyle.Solid, colorPrimary)
        borderRadius(borderRadiusSmall)
        cursor("pointer")
        fontSize(14.px)
    }

    // Modal styles
    val modalOverlay by style {
        position(Position.Fixed)
        top(0.px)
        left(0.px)
        right(0.px)
        bottom(0.px)
        backgroundColor(Color("rgba(0,0,0,0.5)"))
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        property("z-index", "1000")
    }
    val modalContent by style {
        backgroundColor(colorWhite)
        padding(24.px)
        borderRadius(borderRadiusMedium)
        maxWidth(400.px)
        width(90.percent)
        property("box-shadow", "0 4px 20px rgba(0,0,0,0.2)")

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                padding(16.px)
                width(95.percent)
            }
        }
    }
    val modalButtons by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        marginTop(20.px)

        // Mobile responsive
        media(mediaMaxWidth(768.px)) {
            style {
                flexDirection(FlexDirection.Column)
                gap(8.px)
            }
        }
    }

    // Notification badge style
    val notificationBadge by style {
        position(Position.Absolute)
        top((-4).px)
        right((-4).px)
        backgroundColor(Color("#f44336"))
        color(Color.white)
        fontSize(10.px)
        fontWeight("bold")
        minWidth(16.px)
        height(16.px)
        borderRadius(8.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        padding(0.px, 4.px)
        property("box-shadow", "0 1px 3px rgba(0,0,0,0.3)")
    }

    // Keyframes for animations
    val keyframes by style {
        property("@keyframes spin", """
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        """)
        property("@keyframes pulse", """
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
        """)
    }

    private fun mediaMaxWidth(width: CSSSizeValue<CSSUnit.px>) = "(max-width: ${width.value})"
}
